"use client";
import { DrawerHeader } from "@/components/DrawerHeader";
import { usePathname } from "@/hooks/usePathname";
import { useRouter } from "@/hooks/useRouter";
import { pathToRegex } from "@/utils/pathToRegex";
import { useTranslations } from "next-intl";

export enum HeaderType {
  None = "none",
  Default = "default",
  Custom = "custom",
  HiddenSearch = "hiddenSearch",
}

const routesByHeaderType: {
  [key: string]: string[];
} = {
  [HeaderType.None]: ["/search", "/barcode", "/forbidden"],
  [HeaderType.Custom]: [
    "/account",
    "/account/preferences",
    "/barcode/search",
    "/orders/[orderNumber]/share/*",
    "/orders/[orderNumber]",
  ],
  [HeaderType.HiddenSearch]: ["/auth/login"],
};

const elementsByPath: {
  [key: string]: React.FC;
} = {
  "/account": () => {
    const router = useRouter();

    return (
      <DrawerHeader
        onCloseButtonClick={() => {
          router.push("/");
        }}
      />
    );
  },
  "/account/preferences": () => {
    const t = useTranslations("preferences");
    const router = useRouter();

    return (
      <DrawerHeader
        title={t("language_and_store")}
        onBackButtonClick={() => {
          router.replace("/account");
        }}
        onCloseButtonClick={() => {
          router.push("/");
        }}
      />
    );
  },
  "/barcode/search": () => {
    const router = useRouter();

    return (
      <DrawerHeader
        onBackButtonClick={() => {
          router.back();
        }}
      />
    );
  },
  "/orders/[orderNumber]/share/*": () => {
    const router = useRouter();

    return (
      <DrawerHeader
        onCloseButtonClick={() => {
          router.back();
        }}
      />
    );
  },
  "/orders/[orderNumber]": () => {
    const router = useRouter();

    return (
      <DrawerHeader
        title="Order"
        onBackButtonClick={() => {
          router.replace("/orders");
        }}
      />
    );
  },
};

export const useHeaderType = () => {
  const pathName = usePathname();
  const { match, type } = findHeaderTypeByRoute(pathName);

  switch (type) {
    case HeaderType.Custom:
      return {
        type,
        Element: elementsByPath[match],
      };
    case HeaderType.None:
      return {
        type,
        Element: () => <></>,
      };
    default:
      return {
        type,
        Element: () => <></>,
        hideSearch: !!match,
      };
  }
};

function findHeaderTypeByRoute(path: string) {
  for (const [headerType, routes] of Object.entries(routesByHeaderType)) {
    const match = routes.find((route) => pathToRegex(route).test(path));
    if (match) {
      return {
        type:
          headerType === HeaderType.HiddenSearch
            ? HeaderType.Default
            : headerType,
        match,
      };
    }
  }

  return {
    type: HeaderType.Default,
    match: "",
  };
}
